// src/pages/Playlists.js
import React, { useContext, useState, useEffect } from 'react';
import { PlaylistContext } from '../context/PlaylistContext';
import AccordionPanel from '../components/AccordionPanel';
import { useFirebase } from '../contexts/FirebaseContext';
import tipsOnIcon from '../images/icons/tips-on.svg';
import tipsOffIcon from '../images/icons/tips-off.svg';
import '../styles/Playlists.css'; // Import the CSS for Playlists component
import '../styles/Home.css'; // Import Home.css for loading-sentences styling
import logger from '../utils/logger'; // Import logger utility
import apiClient from '../utils/apiClient'; // Import apiClient utility

// Helper function to capitalize a string.
const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const Playlists = () => {
    const { playlists, refreshPlaylists } = useContext(PlaylistContext);
    const [openPanel, setOpenPanel] = useState(null);
    // Get initial settings from localStorage if available
    const savedDaysOfReview = localStorage.getItem("daysOfReview");
    const [settings, setSettings] = useState({
        daysOfReview: savedDaysOfReview ? Number(savedDaysOfReview) : 4
    });
    const { isAuthenticated, getToken } = useFirebase();
    const [showTips, setShowTips] = useState(false);
    const [tipsRead, setTipsRead] = useState(localStorage.getItem("tipsRead") === "true");
    const [isClosing, setIsClosing] = useState(false);
    const [activeLanguage, setActiveLanguage] = useState(null);

    logger.debug("Playlists.js - Initial playlists:", playlists);
    logger.debug("Playlists.js - Initial settings:", settings);

    // Fetch settings on mount.
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Check if we've recently fetched settings (within the last 20 seconds)
                const now = Date.now();
                if (window.lastSettingsFetchTime && (now - window.lastSettingsFetchTime < 20000)) {
                    logger.debug('Playlists - Using cached settings (fetched in the last 20s)');
                    
                    // Create settings object from localStorage
                    const data = {
                        daysOfReview: Number(localStorage.getItem("daysOfReview") || 4),
                        learningLanguage: localStorage.getItem("learningLanguage") || "Spanish",
                        languageLevel: localStorage.getItem("languageLevel") || "Beginner"
                    };
                    
                    // Update settings in state
                    setSettings(data);
                    
                    // Force refresh playlists to use the new settings
                    setTimeout(() => {
                        refreshPlaylists();
                    }, 500);
                    
                    return;
                }
                
                let data;
                if (isAuthenticated) {
                    // Prevent duplicate calls while a fetch is in progress
                    if (window.settingsFetchInProgress) {
                        logger.debug('Playlists - Settings fetch already in progress, skipping duplicate call');
                        return;
                    }
                    
                    window.settingsFetchInProgress = true;
                    
                    // Get authentication token
                    const token = await getToken();
                    logger.debug("Playlists - Firebase token obtained");

                    // const response = await fetch('/api/settings', {
                    //     credentials: 'include',
                    //     headers: {
                    //         'Authorization': `Bearer ${token}`
                    //     }
                    // });

                    const response = await apiClient.get('/api/settings', {
                        // apiClient handles Authorization headers by default
                        // If 'credentials: include' is strictly necessary and not handled by apiClient by default,
                        // this might need adjustment in apiClient or a specific option here if supported.
                    });
                    
                    // Update the last fetch time and clear the in-progress flag
                    window.lastSettingsFetchTime = Date.now();
                    window.settingsFetchInProgress = false;
                    
                    if (!response.ok) throw new Error("Failed to fetch settings");
                    data = await response.json();
                    logger.debug("Playlists - Settings fetched from server:", data);
                } else {
                    // Fall back to localStorage
                    logger.debug("Playlists - Using localStorage settings");
                    data = {
                        daysOfReview: Number(localStorage.getItem("daysOfReview") || 4),
                        learningLanguage: localStorage.getItem("learningLanguage") || "Spanish",
                        languageLevel: localStorage.getItem("languageLevel") || "Beginner"
                    };
                }

                // Update settings in state and localStorage
                setSettings(data);
                localStorage.setItem("daysOfReview", data.daysOfReview);
                logger.debug("Playlists - Final settings being used:", data);

                // Force refresh playlists to use the new settings
                setTimeout(() => {
                    refreshPlaylists();
                }, 500);
            } catch (error) {
                // Clear the in-progress flag in case of error
                if (isAuthenticated) {
                    window.settingsFetchInProgress = false;
                }
                
                logger.error("Playlists - Error fetching settings:", error);
                
                // Fall back to localStorage
                const data = {
                    daysOfReview: Number(localStorage.getItem("daysOfReview") || 4),
                    learningLanguage: localStorage.getItem("learningLanguage") || "Spanish",
                    languageLevel: localStorage.getItem("languageLevel") || "Beginner"
                };
                
                setSettings(data);
                
                // Force refresh playlists despite the error
                setTimeout(() => {
                    refreshPlaylists();
                }, 500);
            }
        };

        fetchSettings();
    }, [isAuthenticated, getToken, refreshPlaylists]);

    // Group playlists by language.
    const groups = playlists.reduce((acc, pl) => {
        const langKey =
            pl.language && pl.language.toLowerCase() !== "default"
                ? pl.language.toLowerCase()
                : "default";
        if (!acc[langKey]) {
            acc[langKey] = [];
        }
        acc[langKey].push(pl);
        return acc;
    }, {});

    logger.debug("Playlists.js - Grouped playlists:", groups);

    // Filter groups to include only those with at least one sentence.
    const groupsEntries = Object.entries(groups).filter(([lang, plArray]) => {
        const totalSentences = plArray.reduce(
            (sum, playlist) => sum + (playlist.sentences ? playlist.sentences.length : 0),
            0
        );
        return totalSentences > 0;
    });

    // Sort groupsEntries to ensure consistent order
    groupsEntries.sort(([langA], [langB]) => {
        // Move 'default' to the end if it exists
        if (langA === 'default') return 1;
        if (langB === 'default') return -1;
        // Otherwise sort alphabetically
        return langA.localeCompare(langB);
    });

    // Helper to find the latest playlist for a given language
    const getLatestPlaylistId = (language) => {
        if (!groups[language]) return null;
        
        // Sort playlists by created/updated date if available, otherwise by name
        // Assuming playlist names are in date format or at least chronological
        const sortedPlaylists = [...groups[language]].sort((a, b) => {
            // If we have createdAt or updatedAt fields, use those
            if (a.createdAt && b.createdAt) {
                return new Date(b.createdAt) - new Date(a.createdAt);
            }
            if (a.updatedAt && b.updatedAt) {
                return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
            
            // Otherwise sort by name (assuming names contain dates like "Day 5")
            // Extract numbers from playlist names for numerical comparison
            const aNum = a.name.match(/\d+/);
            const bNum = b.name.match(/\d+/);
            
            if (aNum && bNum) {
                return parseInt(bNum[0]) - parseInt(aNum[0]);
            }
            
            // Fallback to alphabetical sorting if no numbers found
            return b.name.localeCompare(a.name);
        });
        
        if (sortedPlaylists.length === 0) return null;
        
        const latestPlaylist = sortedPlaylists[0];
        return `${(latestPlaylist.language || "default").trim().toLowerCase()}-${latestPlaylist.name.trim().toLowerCase()}-0`;
    };

    // Set active language to first available language if not already set
    useEffect(() => {
        if (groupsEntries.length > 0) {
            if (!activeLanguage) {
                const firstLanguage = groupsEntries[0][0];
                setActiveLanguage(firstLanguage);
            }
        }
    }, [groupsEntries, activeLanguage]);

    // Effect to update open panel when active language changes
    useEffect(() => {
        if (activeLanguage) {
        }
    }, [activeLanguage]);

    logger.debug("Playlists.js - Filtered group entries:", groupsEntries);

    // Toggle function: if clicking an already open panel, close it; otherwise, open it.
    const togglePanel = (id) => {
        setOpenPanel((prevId) => (prevId === id ? null : id));
    };

    // Handle language tab change
    const handleLanguageChange = (lang) => {
        setActiveLanguage(lang);
    };

    // Handle tips popup open/close
    const handleTipsClick = () => {
        setShowTips(true);
    };

    // Handle tips popup dismissal
    const handleTipsDismiss = () => {
        setIsClosing(true);
        // Delay the actual closing to allow for the animation to play
        setTimeout(() => {
            setShowTips(false);
            setTipsRead(true);
            setIsClosing(false);
            localStorage.setItem("tipsRead", "true");
        }, 300); // Match this to the animation duration
    };

    return (
        <div className="playlists-container">
            <div className="page-title">Playlists</div>

            {groupsEntries.length === 0 ? (
                <div className="loading-sentences" style={{ marginTop: "120px" }}>No playlists available. Add some sentences first.</div>
            ) : (
                <div className="languageGroupBox">
                    <div className="language-tabs">
                        {groupsEntries.map(([lang]) => {
                            const langTitle = lang === "default" ? "Default" : capitalize(lang);
                            return (
                                <div
                                    key={lang}
                                    className={`language-tab ${activeLanguage === lang ? 'active' : ''}`}
                                    onClick={() => handleLanguageChange(lang)}
                                >
                                    {langTitle}
                                </div>
                            );
                        })}
                    </div>
                    
                    {groupsEntries.map(([lang, playlistsArray]) => (
                        <div 
                            key={lang} 
                            className={`language-content ${activeLanguage === lang ? 'active' : ''}`}
                        >
                            {playlistsArray.map((pl, index) => {
                                const panelId = `${(pl.language || "default").trim().toLowerCase()}-${pl.name.trim().toLowerCase()}-${index}`;
                                return (
                                    <AccordionPanel
                                        key={panelId}
                                        playlist={pl}
                                        id={panelId}
                                        isOpen={openPanel === panelId}
                                        onToggle={togglePanel}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {/* Tips Feature - positioned at bottom right */}
            <div className="tips-container" style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                display: 'flex',
                alignItems: 'center',
                zIndex: 900
            }}>
                <img
                    src={tipsRead ? tipsOffIcon : tipsOnIcon}
                    alt="Tips"
                    style={{
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        filter: tipsRead
                            ? 'grayscale(100%)'
                            : 'brightness(1.2) sepia(1) hue-rotate(-10deg) saturate(5) drop-shadow(0 0 3px #ffcc00)'
                    }}
                    onClick={handleTipsClick}
                />

                {/* "Read me first" white container - only shows if tips haven't been read */}
                {!tipsRead && (
                    <div
                        className="tips-popup-readme"
                        onClick={handleTipsClick}
                    >
                        Read me first!
                    </div>
                )}
            </div>

            {/* Tips Popup */}
            {showTips && (
                <div className="tips-popup-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    animation: isClosing ? 'fadeOut 0.3s ease-in-out' : 'fadeIn 0.3s ease-in-out'
                }}>
                    <div className="tips-popup" style={{
                        backgroundColor: '#f0f0f0',
                        borderRadius: '8px',
                        padding: '20px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        animation: isClosing ? 'scaleOut 0.3s ease-in-out' : 'scaleIn 0.3s ease-in-out',
                        textAlign: 'left' // Ensures text in the tips popup is left-aligned
                    }}>
                        <h2 className="tips-popup-title">Tips:</h2>
                        <hr className="tips-popup-divider" />
                        <div className="tips-popup-content">
                            <p className="tips-popup-paragraph">
                                <span className="tips-popup-number">1.</span> To get the most out of your sentence repetition with the audio player, make sure to just let the audio files play. Don't pause to catch up, and don't worry if you can't always keep up. That's how you'll train your mind to slowly acclimate to the natural speed of the language. In both speaking and understanding it.
                            </p>
                            <p className="tips-popup-paragraph">
                                <span className="tips-popup-number">2.</span> But the true secret behind quick absorption of a new language and its meaning requires the participation of your imagination. When repeating the sentences, I strongly recommend visualizing the meaning of the words that you are saying out loud. The word "caterpillar" is just a sound by itself. You can memorize that sound, but the meaning that the mind produces when hearing it is a whole different thing. It's the other half of the puzzle. To really establish the connection between the sounds you produce and their meanings in your mind, imagine that caterpillar when speaking about it. You do it automatically in your own language without thinking. Now, you'll have to put some conscious effort at first, and it may feel odd. However, very quickly, it'll become as second nature as your mother tongue! Good luck!
                            </p>
                        </div>
                        <div className="tips-popup-button-container">
                            <button
                                onClick={handleTipsDismiss}
                                className="tips-popup-dismiss-button"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Playlists;
